import { ENV } from './env';
export class ApiError extends Error {
    status;
    body;
    constructor(status, body) {
        super(`API error ${status}: ${body}`);
        this.status = status;
        this.body = body;
        this.name = 'ApiError';
    }
}
// ---------------------------------------------------------------------------
// Cart session - localStorage header fallback for Safari ITP / 3rd-party
// cookie deprecation. The backend echoes the resolved ID back via the same
// header so the client can refresh the value on every response.
// ---------------------------------------------------------------------------
const CART_SESSION_KEY = 'lpc.cart-session';
function getCartSessionId() {
    if (typeof window === 'undefined')
        return null;
    return window.localStorage.getItem(CART_SESSION_KEY);
}
function setCartSessionId(id) {
    if (typeof window === 'undefined')
        return;
    window.localStorage.setItem(CART_SESSION_KEY, id);
}
/** Generate a UUID v4 - prefers native `crypto.randomUUID()`, falls back
 * to a small RFC-4122 polyfill for older browsers (Safari 14 etc). */
function randomUuidV4() {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
        return crypto.randomUUID();
    }
    const bytes = new Uint8Array(16);
    if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
        crypto.getRandomValues(bytes);
    }
    else {
        for (let i = 0; i < 16; i++)
            bytes[i] = Math.floor(Math.random() * 256);
    }
    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;
    const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
    return (hex.slice(0, 8) + '-' + hex.slice(8, 12) + '-' + hex.slice(12, 16) + '-' +
        hex.slice(16, 20) + '-' + hex.slice(20, 32));
}
const MUTATING_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);
/**
 * Fetch wrapper with credentials: 'include' and base URL from env.
 * Attaches X-Cart-Session header from localStorage on every request and
 * persists the response header value back to localStorage (covers the
 * initial-mint case and any server-side session refresh).
 *
 * Mutating requests (POST/PUT/PATCH/DELETE) also carry an
 * `Idempotency-Key: <uuid v4>` header so the backend can replay the
 * first-execution response on retry (Phase E). One key per call; if the
 * caller wants to force a replay (e.g. retry the SAME submit), it MUST
 * pre-set the header on `init.headers` so this wrapper does not
 * overwrite it.
 *
 * Throws ApiError on non-2xx responses.
 */
export async function api(path, init) {
    const url = `${ENV.apiBaseUrl}${ENV.apiPrefix}${path}`;
    const sessionId = getCartSessionId();
    const headers = new Headers(init?.headers);
    if (sessionId)
        headers.set('X-Cart-Session', sessionId);
    const method = (init?.method ?? 'GET').toUpperCase();
    if (MUTATING_METHODS.has(method) && !headers.has('Idempotency-Key')) {
        headers.set('Idempotency-Key', randomUuidV4());
    }
    const res = await fetch(url, { credentials: 'include', ...init, headers });
    const returnedSessionId = res.headers.get('X-Cart-Session');
    if (returnedSessionId)
        setCartSessionId(returnedSessionId);
    if (!res.ok) {
        const body = await res.text().catch(() => '');
        throw new ApiError(res.status, body);
    }
    return res.json();
}
export async function listPublishedProducts(cursor, limit = 20) {
    const params = new URLSearchParams({ limit: String(limit) });
    if (cursor)
        params.set('cursor', cursor);
    return api(`/catalog/online?${params}`);
}
export async function getProductBySlug(slug) {
    return api(`/catalog/online/${slug}`);
}
export async function getSignatureProducts() {
    return api(`/catalog/online/signature`);
}
export async function getRelatedProducts(slug, limit = 6) {
    return api(`/catalog/online/${slug}/related?limit=${limit}`);
}
export async function getBusinessHours() {
    return api(`/business-hours`);
}
export async function getInstagramRecent() {
    return api('/instagram/recent');
}
export async function getCart() {
    return api('/cart');
}
export async function upsertCartItem(productProfileId, body) {
    return api(`/cart/items/${productProfileId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
    });
}
export async function deleteCartItem(productProfileId) {
    return api(`/cart/items/${productProfileId}`, { method: 'DELETE' });
}
export async function clearCart() {
    return api('/cart', { method: 'DELETE' });
}
/**
 * Place an anonymous hold against `inventory_items.quantity - SUM(active reservations)`.
 * The cart session cookie / X-Cart-Session header (handled by `api()`) is the
 * identity - no JWT.
 *
 * Backend returns 422 with `availableQuantity` when over-reserving; callers
 * should propagate to the UI so the quantity stepper can re-clamp.
 */
export async function reserveStock(productProfileId, quantity) {
    return api('/cart/reserve', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ productProfileId, quantity }),
    });
}
/**
 * Release a previously-created hold. Idempotent on already-consumed /
 * already-released / already-expired (server returns 204 regardless).
 */
export async function releaseReservation(reservationId) {
    await api(`/cart/reserve/${reservationId}`, { method: 'DELETE' });
}
